import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ShieldAlert, RefreshCw, Lock, Zap, Send, Ban, UserCog, Timer, BellRing } from "lucide-react";

/**
 * THOM Escrow™ Workspace — asset-agnostic transaction control room.
 *
 * Every asset type flows through one workspace. Filters by workflow / country /
 * status / asset type. Detail drawer allows advance / cancel / dispute / resolve.
 */
export default function EscrowWorkspace() {
  const [workflows, setWorkflows] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: "any", asset_type: "", country: "", workflow_key: "any" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (filters.status && filters.status !== "any") params.status = filters.status;
      if (filters.asset_type) params.asset_type = filters.asset_type;
      if (filters.country) params.country = filters.country;
      if (filters.workflow_key && filters.workflow_key !== "any") params.workflow_key = filters.workflow_key;
      const [wf, r] = await Promise.all([
        api.get("/escrow-v2/workflows"),
        api.get("/escrow-v2", { params }),
      ]);
      setWorkflows(wf.data.items || []);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load escrow transactions");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openRow = async (r) => {
    const { data } = await api.get(`/escrow-v2/${r.reference}`);
    setSelected(data);
  };

  const advance = async () => {
    if (!selected) return;
    try {
      await api.post(`/escrow-v2/${selected.reference}/advance`, { note });
      toast.success(`${selected.reference} advanced`);
      setNote("");
      await openRow(selected);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Advance failed"); }
  };

  const cancelTx = async () => {
    if (!selected) return;
    if (!confirm(`Cancel ${selected.reference}? Refund eligibility will be evaluated automatically.`)) return;
    try {
      const r = await api.post(`/escrow-v2/${selected.reference}/cancel`, { reason: note });
      toast.success(`Result: ${r.data.status}`);
      setNote("");
      await openRow(selected);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const openDispute = async () => {
    if (!selected || !disputeReason.trim()) return;
    try {
      await api.post(`/escrow-v2/${selected.reference}/dispute`, { reason: disputeReason, detail: note });
      toast.success("Dispute filed");
      setDisputeReason(""); setNote("");
      await openRow(selected);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dispute failed"); }
  };

  const resolveDispute = async (dsp, resolution) => {
    try {
      await api.post(`/escrow-v2/${selected.reference}/dispute/${dsp.id}/resolve`, { resolution, note });
      toast.success(`Dispute ${resolution}`);
      await openRow(selected);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="kicker">Executive · THOM Escrow™</div>
            <h1 className="display text-3xl">Escrow Transaction Workspace</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Asset-agnostic escrow control room. Vehicles · property · mining · commodities · equipment · government
              assets · banking collateral · insurance claims · agriculture · logistics — one engine.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="escrow-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
            <Link to="/ops"><Button size="sm" variant="outline">Ops Centre</Button></Link>
          </div>
        </div>

        {/* Filters */}
        <div className="grid md:grid-cols-5 gap-2 mb-4">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="escrow-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Asset type" value={filters.asset_type} onChange={(e) => setFilters({ ...filters, asset_type: e.target.value })} data-testid="escrow-asset-type" />
          <Input placeholder="Country" value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} data-testid="escrow-country" />
          <Select value={filters.workflow_key} onValueChange={(v) => setFilters({ ...filters, workflow_key: v })}>
            <SelectTrigger data-testid="escrow-workflow-filter"><SelectValue placeholder="Workflow" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All workflows</SelectItem>
              {workflows.map(w => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={load} data-testid="escrow-apply">Apply</Button>
        </div>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-4">
          {/* Rows */}
          <div className="border border-border bg-card overflow-hidden">
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-border">
              {items.length === 0 && !busy && (
                <div className="p-8 text-center text-sm text-muted-foreground">No escrow transactions match the current filters. Create one from any asset detail page or via <code className="mono text-[10.5px]">POST /api/escrow-v2</code>.</div>
              )}
              {items.map((r) => (
                <button
                  key={r.reference}
                  onClick={() => openRow(r)}
                  data-testid={`escrow-row-${r.reference}`}
                  className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors ${selected?.reference === r.reference ? "bg-primary/5 border-l-2 border-l-primary" : ""}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="mono text-[11px] uppercase tracking-widest text-primary">{r.reference}</div>
                      <div className="text-sm font-medium mt-0.5">{r.asset_ref}</div>
                      <div className="text-[11.5px] text-muted-foreground">{r.country} · <span className="mono uppercase text-[10px]">{r.asset_type}</span> · {r.workflow_label}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">{r.current_stage.replace(/_/g, ' ')}</span>
                      <span className={`mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${r.status === 'completed' ? 'bg-emerald-500/10 text-emerald-700' : r.status === 'refunded' ? 'bg-amber-500/10 text-amber-700' : 'bg-slate-500/10 text-slate-700'}`}>{r.status}</span>
                      {r.is_disputed && <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-red-500/10 text-red-700 inline-flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Disputed</span>}
                    </div>
                  </div>
                  <div className="mt-1 mono text-[10.5px] text-muted-foreground">{r.currency} {(r.amount_total || 0).toLocaleString()}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Detail drawer */}
          <div className="border border-border bg-card p-5 sticky top-24 self-start max-h-[85vh] overflow-y-auto">
            {!selected && <div className="text-sm text-muted-foreground">Select an escrow row to view the workflow.</div>}
            {selected && (
              <div data-testid="escrow-detail">
                <div className="mono text-[11px] uppercase tracking-widest text-primary">{selected.reference}</div>
                <h3 className="text-lg font-semibold mt-1">{selected.asset_ref}</h3>
                <div className="mono text-[11px] text-muted-foreground mt-0.5">
                  {selected.country} · {selected.currency} {(selected.amount_total || 0).toLocaleString()} · <span className="uppercase">{selected.asset_type}</span>
                </div>
                <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground mt-2">Workflow: {selected.workflow_label}</div>

                {/* Stages */}
                <div className="mt-5 space-y-1.5">
                  {(selected.stages || []).map((s, i) => {
                    const done = s.status === "complete";
                    const active = s.key === selected.current_stage && selected.status === "active";
                    return (
                      <div key={s.key} className={`flex items-start gap-2 py-1 px-2 rounded-sm ${active ? "bg-primary/5 border-l-2 border-l-primary" : ""}`} data-testid={`escrow-stage-${s.key}`}>
                        <div className={`w-4 h-4 mt-0.5 rounded-full flex items-center justify-center mono text-[8.5px] ${done ? "bg-primary text-primary-foreground" : active ? "bg-amber-500 text-white" : "bg-muted text-muted-foreground"}`}>{done ? "✓" : i + 1}</div>
                        <div className="flex-1">
                          <div className="text-[12.5px] font-medium">{s.label}</div>
                          <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">Role: {s.required_role} {s.admin_required && <span className="text-amber-700 ml-1">· admin</span>}</div>
                          {s.note && <div className="text-[11px] text-muted-foreground mt-0.5 italic">"{s.note}"</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                {selected.status === "active" && (
                  <div className="mt-5 border-t border-border pt-4">
                    <textarea
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Note (required by some workflows before advancing)"
                      className="w-full text-xs border border-border rounded-sm p-2 bg-background"
                      data-testid="escrow-note-input"
                    />
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Button size="sm" onClick={advance} data-testid="escrow-advance-btn">
                        <ArrowRight className="h-3.5 w-3.5 mr-1" /> Advance stage
                      </Button>
                      <Button size="sm" variant="outline" onClick={cancelTx} data-testid="escrow-cancel-btn">
                        <Lock className="h-3.5 w-3.5 mr-1" /> Cancel (evaluate refund)
                      </Button>
                    </div>
                    <div className="mt-3">
                      <Input placeholder="Dispute reason (buyer/seller/regulator)" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} className="h-8 text-xs" data-testid="escrow-dispute-reason" />
                      <Button size="sm" variant="outline" onClick={openDispute} disabled={!disputeReason.trim()} className="mt-2" data-testid="escrow-dispute-btn">
                        <ShieldAlert className="h-3.5 w-3.5 mr-1" /> File dispute
                      </Button>
                    </div>
                  </div>
                )}

                {/* Disputes */}
                {(selected.disputes || []).length > 0 && (
                  <div className="mt-5 border-t border-border pt-4">
                    <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5" /> Disputes ({selected.disputes.length})</div>
                    <ol className="space-y-2">
                      {selected.disputes.map((d) => (
                        <li key={d.id} className="text-[11.5px] border border-border rounded-sm p-2" data-testid={`escrow-dispute-${d.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="mono text-[10px] uppercase text-primary">{d.status}</span>
                            <span className="mono text-[9.5px] text-muted-foreground">{new Date(d.opened_at).toLocaleString()}</span>
                          </div>
                          <div className="mt-1"><strong>{d.reason}</strong>{d.detail ? ` — ${d.detail}` : ""}</div>
                          {d.status === "open" && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              <Button size="sm" variant="outline" onClick={() => resolveDispute(d, "release")}>Release funds</Button>
                              <Button size="sm" variant="outline" onClick={() => resolveDispute(d, "refund")}>Refund buyer</Button>
                              <Button size="sm" variant="outline" onClick={() => resolveDispute(d, "dismiss")}>Dismiss</Button>
                            </div>
                          )}
                          {d.resolution && <div className="mt-1 mono text-[10px] text-emerald-700">Resolved: {d.resolution}</div>}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Parties */}
                <div className="mt-5 border-t border-border pt-4">
                  <div className="text-xs font-semibold mb-2">Parties</div>
                  <ul className="space-y-1 text-[11.5px]">
                    {(selected.parties || []).map((p, i) => (
                      <li key={i} className="flex items-center justify-between">
                        <span><span className="mono uppercase text-[10px] text-primary">{p.role}</span> · {p.name}{p.organisation ? ` · ${p.organisation}` : ""}</span>
                        <span className="mono text-[10px] text-muted-foreground">{p.email || p.telephone || ""}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Invitations admin */}
                <InvitationAdmin reference={selected.reference} onChange={() => openRow(selected)} />

                <div className="mt-4 mono text-[10.5px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Zap className="h-3 w-3" /> All actions audited · actor · IP · timestamp</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

/** Invitation admin panel — resend / revoke / replace recipient / extend expiry. */
function InvitationAdmin({ reference, onChange }) {
  const [rows, setRows] = useState([]);
  const [openRow, setOpenRow] = useState(null);
  const [replaceEmail, setReplaceEmail] = useState("");
  const [replaceName, setReplaceName] = useState("");
  const [replacePhone, setReplacePhone] = useState("");
  const extraHours = 168;

  const load = async () => {
    try { const r = await api.get(`/escrow-v2/${reference}/invitations`); setRows(r.data.items || []); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reference]);

  const act = async (action, payload, msg) => {
    try {
      await api.post(`/escrow-v2/${reference}/invitations/${action}`, payload);
      toast.success(msg);
      setOpenRow(null); setReplaceEmail(""); setReplaceName(""); setReplacePhone("");
      await load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
  };

  return (
    <div className="mt-5 border-t border-border pt-4">
      <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Invitations ({rows.length})</div>
      {rows.length === 0 && <div className="text-[11.5px] text-muted-foreground">No invitations dispatched yet.</div>}
      <ul className="space-y-2">
        {rows.map(r => {
          const isOpen = openRow === r.email;
          return (
            <li key={r.email + r.created_at} className="text-[11.5px] border border-border rounded-sm p-2" data-testid={`invite-${r.email}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="mono text-[10px] uppercase text-primary">{r.role} · {r.status}</div>
                  <div>{r.email || r.telephone}{r.reminders_sent ? ` · ${r.reminders_sent} reminder(s)` : ""}</div>
                  <div className="mono text-[9.5px] text-muted-foreground">Expires {new Date(r.expires_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <button className="text-[10px] uppercase mono tracking-widest px-1.5 py-0.5 rounded-sm border border-border hover:bg-muted" data-testid={`invite-resend-${r.email}`} onClick={() => act("resend", { email: r.email }, "Reminder dispatched")}><Send className="h-3 w-3 inline mr-1" />Resend</button>
                  <button className="text-[10px] uppercase mono tracking-widest px-1.5 py-0.5 rounded-sm border border-border hover:bg-muted" data-testid={`invite-extend-${r.email}`} onClick={() => act("extend", { email: r.email, extra_hours: extraHours }, "Expiry extended")}><Timer className="h-3 w-3 inline mr-1" />+{extraHours}h</button>
                  <button className="text-[10px] uppercase mono tracking-widest px-1.5 py-0.5 rounded-sm border border-border hover:bg-muted" data-testid={`invite-replace-${r.email}`} onClick={() => setOpenRow(isOpen ? null : r.email)}><UserCog className="h-3 w-3 inline mr-1" />Replace</button>
                  {r.status !== "accepted" && (
                    <button className="text-[10px] uppercase mono tracking-widest px-1.5 py-0.5 rounded-sm border border-red-500/40 text-red-700 hover:bg-red-50" data-testid={`invite-revoke-${r.email}`} onClick={() => act("revoke", { email: r.email }, "Revoked")}><Ban className="h-3 w-3 inline mr-1" />Revoke</button>
                  )}
                </div>
              </div>
              {isOpen && (
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                  <input className="h-7 text-[11px] border border-border rounded-sm px-2 bg-background" placeholder="New email" value={replaceEmail} onChange={(e) => setReplaceEmail(e.target.value)} data-testid={`invite-replace-email-${r.email}`} />
                  <input className="h-7 text-[11px] border border-border rounded-sm px-2 bg-background" placeholder="Name" value={replaceName} onChange={(e) => setReplaceName(e.target.value)} />
                  <input className="h-7 text-[11px] border border-border rounded-sm px-2 bg-background" placeholder="Phone" value={replacePhone} onChange={(e) => setReplacePhone(e.target.value)} />
                  <button className="col-span-3 text-[10px] uppercase mono tracking-widest px-2 py-1 rounded-sm bg-primary text-primary-foreground" onClick={() => act("replace", { old_email: r.email, new_email: replaceEmail, new_name: replaceName, new_telephone: replacePhone }, "Recipient replaced")}>Replace + dispatch new invitation</button>
                </div>
              )}
              {r.delivery_status && Object.keys(r.delivery_status).length > 0 && (
                <div className="mt-1 mono text-[9.5px] text-muted-foreground">Delivery: {Object.entries(r.delivery_status).map(([k, v]) => `${k}:${String(v).slice(0, 12)}`).join(" · ")}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

